// @ts-nocheck baseline 2026-02-16
import { addDays, startOfWeek, addWeeks, formatISO } from "date-fns";
import { type DayOfTheWeek, type DaysOfTheWeek } from "@visioni/schedules";
import type { AlarmSchedule } from "@visioni/a3s-alarm-schedules";
import { AlarmException } from "@src/services/api/types";
import type { CameraArmingType } from "@src/features/alarms/utils/alarm-utils";
import { UnistylesRuntime } from "react-native-unistyles";
import type { WeeklyRoutineWindow } from "@src/services/api/types";

export const SLOTS_PER_DAY = 24;
export const MINUTES_PER_SLOT = 60;

export type SlotType = "disarmed" | "armed" | string; // string for exception IDs
export type DaySlots = SlotType[]; // length 24 (1 hour each)
export type DailySlotMap = Record<string, DaySlots>; // key = ISO date (yyyy-MM-dd)

// Get exception colors from theme
export function getExceptionColors(): string[] {
  const theme = UnistylesRuntime.getTheme();
  return [
    theme.colors.blue[600],
    theme.colors.warning[600],
    theme.colors.danger[600],
    theme.colors.purple[600],
    theme.colors.indigo[600],
    theme.colors.teal[600],
    theme.colors.cyan[600],
  ];
}

export function emptyDay(): DaySlots {
  return Array(SLOTS_PER_DAY).fill("disarmed") as DaySlots;
}

export function buildWeeks(centerDate: Date, weeksBefore = 52, weeksAfter = 52): Date[][] {
  const firstWeekStart = addWeeks(startOfWeek(centerDate, { weekStartsOn: 1 }), -weeksBefore);
  const total = weeksBefore + weeksAfter + 1;
  return Array.from({ length: total }, (_, wi) => {
    const weekStart = addWeeks(firstWeekStart, wi);
    return Array.from({ length: 7 }, (_, di) => addDays(weekStart, di));
  });
}

// Map day strings to JavaScript Date.getDay() values (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
const DAY_STRING_TO_NUMBER: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

// Helper to check if a rule is "armed" based on features
export function isRuleArmed(features: Record<string, string>): boolean {
  return Object.values(features).some((state) => state === "armed");
}

// Helper to check if a date is within an effective period
function isDateInEffectivePeriod(
  date: Date,
  effectivePeriod: {
    start: { year: number; month: number; day: number; hour: number; minute: number } | null;
    end: { year: number; month: number; day: number; hour: number; minute: number } | null;
  },
): boolean {
  if (effectivePeriod.start) {
    const startDate = new Date(effectivePeriod.start.year, effectivePeriod.start.month - 1, effectivePeriod.start.day);
    if (date < startDate) return false;
  }
  if (effectivePeriod.end) {
    const endDate = new Date(effectivePeriod.end.year, effectivePeriod.end.month - 1, effectivePeriod.end.day);
    if (date > endDate) return false;
  }
  return true;
}

export type DateTimeRecord = { year: number; month: number; day: number; hour: number; minute: number };

export function isExceptionAllDayRange(start: DateTimeRecord, end: DateTimeRecord): boolean {
  const startsAtMidnight = start.hour === 0 && start.minute === 0;
  if (!startsAtMidnight) {
    return false;
  }

  const startDate = new Date(start.year, start.month - 1, start.day);
  const endDate = new Date(end.year, end.month - 1, end.day);
  const isSameDay = startDate.getTime() === endDate.getTime();

  if (isSameDay) {
    return end.hour === 24 && end.minute === 0;
  }

  const endsAtMidnight = end.hour === 0 && end.minute === 0;
  return endsAtMidnight;
}

function isDateWithinExceptionDayRange(currentDateOnly: Date, start: DateTimeRecord, end: DateTimeRecord): boolean {
  const startDate = new Date(start.year, start.month - 1, start.day);
  const endDate = new Date(end.year, end.month - 1, end.day);

  if (currentDateOnly < startDate || currentDateOnly > endDate) {
    return false;
  }

  const isSameDay = startDate.getTime() === endDate.getTime();

  if (isSameDay) {
    // 00:00-00:00 on the same date is a zero-length no-op.
    return !(end.hour === 0 && end.minute === 0);
  }

  const endsAtMidnight = end.hour === 0 && end.minute === 0;
  if (endsAtMidnight && currentDateOnly.getTime() === endDate.getTime()) {
    return false;
  }

  return true;
}

function isFullDayRangeForDate(currentDateOnly: Date, start: DateTimeRecord, end: DateTimeRecord): boolean {
  const startsAtMidnight = start.hour === 0 && start.minute === 0;
  if (!startsAtMidnight) {
    return false;
  }

  const startDate = new Date(start.year, start.month - 1, start.day);
  const endDate = new Date(end.year, end.month - 1, end.day);
  const isSameDay = startDate.getTime() === endDate.getTime();

  if (isSameDay) {
    return end.hour === 24 && end.minute === 0;
  }

  const endsAtMidnight = end.hour === 0 && end.minute === 0;
  if (!endsAtMidnight) {
    return false;
  }

  return currentDateOnly >= startDate && currentDateOnly < endDate;
}

// Helper to normalize EffectiveTimeRanges to an array
function normalizeTimeRanges(
  timeRanges: unknown,
): Array<{ kind: string; start?: { hour: number; minute: number }; end?: { hour: number; minute: number } }> {
  if (Array.isArray(timeRanges)) {
    return timeRanges as Array<{
      kind: string;
      start?: { hour: number; minute: number };
      end?: { hour: number; minute: number };
    }>;
  }
  return [
    timeRanges as { kind: string; start?: { hour: number; minute: number }; end?: { hour: number; minute: number } },
  ];
}

// Helper to apply a time range to day slots
function applyTimeRangeToSlots(
  timeRange: { kind: string; start?: { hour: number; minute: number }; end?: { hour: number; minute: number } },
  daySlots: DaySlots,
  isArmed: boolean,
): void {
  // Handle all_day time ranges
  if (timeRange.kind === "all_day") {
    // Fill entire day
    for (let i = 0; i < SLOTS_PER_DAY; i++) {
      daySlots[i] = isArmed ? "armed" : "disarmed";
    }
    return;
  }

  // Handle time_range
  if (timeRange.kind === "time_range" && timeRange.start && timeRange.end) {
    const startSlot = Math.floor((timeRange.start.hour * 60 + timeRange.start.minute) / MINUTES_PER_SLOT);
    const endSlot = Math.floor((timeRange.end.hour * 60 + timeRange.end.minute) / MINUTES_PER_SLOT);

    // Handle case where start equals end (0:00-0:00) - treat as no range
    if (startSlot === endSlot) {
      return;
    }

    // Handle time ranges that span midnight (endSlot < startSlot)
    if (endSlot < startSlot) {
      // Fill from startSlot to end of day
      for (let i = startSlot; i < SLOTS_PER_DAY; i++) {
        daySlots[i] = isArmed ? "armed" : "disarmed";
      }
      // Fill from start of day to endSlot (exclusive - endSlot represents when range ends)
      for (let i = 0; i < endSlot; i++) {
        daySlots[i] = isArmed ? "armed" : "disarmed";
      }
    } else {
      // Normal case: fill from startSlot to endSlot (exclusive)
      for (let i = startSlot; i < endSlot; i++) {
        if (i < SLOTS_PER_DAY) {
          daySlots[i] = isArmed ? "armed" : "disarmed";
        }
      }
    }
  }
}

// Helper to add transition times from time ranges
function addTransitionTimesFromTimeRanges(
  timeRanges: Array<{ kind: string; start?: { hour: number; minute: number }; end?: { hour: number; minute: number } }>,
  transitions: TransitionTime[],
  isArmed: boolean,
): void {
  timeRanges.forEach((timeRange) => {
    // Handle all_day time ranges
    if (timeRange.kind === "all_day") {
      transitions.push({
        hour: 0,
        minute: 0,
        isArmed,
      });
      transitions.push({
        hour: 24,
        minute: 0,
        isArmed: !isArmed,
      });
      return;
    }

    // Handle time_range
    if (timeRange.kind === "time_range" && timeRange.start && timeRange.end) {
      // Skip if start equals end (0:00-0:00) - no transitions
      if (timeRange.start.hour === timeRange.end.hour && timeRange.start.minute === timeRange.end.minute) {
        return;
      }

      transitions.push({
        hour: timeRange.start.hour,
        minute: timeRange.start.minute,
        isArmed,
      });
      transitions.push({
        hour: timeRange.end.hour,
        minute: timeRange.end.minute,
        isArmed: !isArmed, // Opposite state after end
      });
    }
  });
}

// Helper to calculate rotating pattern cycle offset and find matching interval
function findRotatingPatternInterval<T extends { days: number; time_ranges?: unknown; is_off_day?: boolean }>(
  date: Date,
  patternStart: { year: number; month: number; day: number },
  pattern: T[],
): { interval: T } | null {
  const patternStartDate = new Date(patternStart.year, patternStart.month - 1, patternStart.day);

  // Calculate total pattern duration
  const patternDuration = pattern.reduce((sum, interval) => sum + interval.days, 0);

  if (patternDuration === 0) {
    return null; // Invalid pattern
  }

  // Calculate days since pattern start
  const daysSinceStart = Math.floor((date.getTime() - patternStartDate.getTime()) / (1000 * 60 * 60 * 24));

  // Get offset within current cycle (handle negative offsets for dates before pattern start)
  let cycleOffset = daysSinceStart % patternDuration;
  if (cycleOffset < 0) {
    cycleOffset = patternDuration + cycleOffset;
  }

  // Find which interval this offset falls into
  let currentOffset = 0;
  for (const interval of pattern) {
    if (cycleOffset >= currentOffset && cycleOffset < currentOffset + interval.days) {
      return { interval };
    }
    currentOffset += interval.days;
  }

  return null; // Should not happen, but safety check
}

export function buildScheduleFromRules(
  schedule: AlarmSchedule,
  exceptions: AlarmException[],
  startDate: Date,
  endDate: Date,
): DailySlotMap {
  const dailySlotMap: DailySlotMap = {};

  // Iterate through each day in the range
  let currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const dateKey = formatISO(currentDate, { representation: "date" });
    const daySlots = emptyDay();
    // Apply schedule rules
    const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday

    schedule.rules.forEach((rule) => {
      // Handle weekly_pattern rules
      if (rule.kind === "weekly_pattern") {
        // Check if date is within effective period
        if (!isDateInEffectivePeriod(currentDate, rule.weekly_pattern.effective_period)) {
          return;
        }

        // Convert day strings to numbers and check if this day matches
        const ruleDays = rule.weekly_pattern.days.map((day) => DAY_STRING_TO_NUMBER[day]);
        if (!ruleDays.includes(dayOfWeek)) {
          return;
        }

        // Check if rule is armed based on features
        const isArmed = isRuleArmed(rule.features);

        // Apply time ranges
        const timeRangesArray = normalizeTimeRanges(rule.weekly_pattern.time_ranges);
        timeRangesArray.forEach((timeRange) => {
          applyTimeRangeToSlots(timeRange, daySlots, isArmed);
        });
      } else if (rule.kind === "rotating_pattern") {
        // Handle rotating_pattern rules
        // Check if date is within effective period
        if (!isDateInEffectivePeriod(currentDate, rule.rotating_pattern.effective_period)) {
          return;
        }

        // Find matching interval for this date
        const result = findRotatingPatternInterval(
          currentDate,
          rule.rotating_pattern.start,
          rule.rotating_pattern.pattern,
        );

        if (!result) {
          return; // Invalid pattern or no matching interval
        }

        const matchingInterval = result.interval;

        // Skip if this is an off day
        if (matchingInterval.is_off_day) {
          return;
        }

        // Check if rule is armed based on features
        const isArmed = isRuleArmed(rule.features);

        // Apply time ranges from the matching interval
        if (matchingInterval.time_ranges) {
          const timeRangesArray = normalizeTimeRanges(matchingInterval.time_ranges);
          timeRangesArray.forEach((timeRange) => {
            applyTimeRangeToSlots(timeRange, daySlots, isArmed);
          });
        }
      } else if (rule.kind === "absolute_range") {
        // Handle absolute_range rules (silence, override, and exceptions)
        const effectivePeriod = rule.absolute_range.effective_period;

        // Check if date is within effective period
        if (!isDateInEffectivePeriod(currentDate, effectivePeriod)) {
          return;
        }

        const slotValue: SlotType = rule.id;

        const start = effectivePeriod.start;
        const end = effectivePeriod.end;

        if (!start || !end) {
          return;
        }

        const ruleStartDate = new Date(start.year, start.month - 1, start.day);
        const ruleEndDate = new Date(end.year, end.month - 1, end.day);
        const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

        // Determine if this is the first day, last day, or a middle day
        const isFirstDay = formatISO(ruleStartDate, { representation: "date" }) === dateKey;
        const isLastDay = formatISO(ruleEndDate, { representation: "date" }) === dateKey;

        // Check if it's an all-day rule
        const isAllDay = isFullDayRangeForDate(currentDateOnly, start, end);

        if (isAllDay) {
          // Fill entire day
          daySlots.fill(slotValue);
        } else {
          let startSlot: number;
          let endSlot: number;

          if (isFirstDay && isLastDay) {
            // Rule starts and ends on the same day
            startSlot = Math.floor((start.hour * 60 + start.minute) / MINUTES_PER_SLOT);
            endSlot = Math.floor((end.hour * 60 + end.minute) / MINUTES_PER_SLOT);
          } else if (isFirstDay) {
            // First day: from start time to end of day
            startSlot = Math.floor((start.hour * 60 + start.minute) / MINUTES_PER_SLOT);
            endSlot = SLOTS_PER_DAY;
          } else if (isLastDay) {
            // Last day: from start of day to end time
            startSlot = 0;
            endSlot = Math.floor((end.hour * 60 + end.minute) / MINUTES_PER_SLOT);
          } else {
            // Middle day: entire day
            startSlot = 0;
            endSlot = SLOTS_PER_DAY;
          }

          for (let i = startSlot; i < endSlot; i++) {
            if (i < SLOTS_PER_DAY) {
              daySlots[i] = slotValue;
            }
          }
        }
      }
    });

    // Apply exceptions on top of schedule
    const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    const exceptionsForDay = exceptions.filter((ex) => {
      try {
        return isDateWithinExceptionDayRange(currentDateOnly, ex.period.start, ex.period.end);
      } catch (error) {
        console.warn(`Failed to parse exception period:`, ex.period, error);
        return false;
      }
    });

    exceptionsForDay.forEach((exception) => {
      const start = exception.period.start;
      const end = exception.period.end;

      const exStartDate = new Date(start.year, start.month - 1, start.day);
      const exEndDate = new Date(end.year, end.month - 1, end.day);

      const isFirstDay = formatISO(exStartDate, { representation: "date" }) === dateKey;
      const isLastDay = formatISO(exEndDate, { representation: "date" }) === dateKey;

      const isAllDay = isFullDayRangeForDate(currentDateOnly, start, end);

      if (isAllDay) {
        daySlots.fill(exception.id);
        return;
      }

      let startSlot: number;
      let endSlot: number;

      if (isFirstDay && isLastDay) {
        startSlot = Math.floor((start.hour * 60 + start.minute) / MINUTES_PER_SLOT);
        endSlot = Math.floor((end.hour * 60 + end.minute) / MINUTES_PER_SLOT);
      } else if (isFirstDay) {
        startSlot = Math.floor((start.hour * 60 + start.minute) / MINUTES_PER_SLOT);
        endSlot = SLOTS_PER_DAY;
      } else if (isLastDay) {
        startSlot = 0;
        endSlot = Math.floor((end.hour * 60 + end.minute) / MINUTES_PER_SLOT);
      } else {
        startSlot = 0;
        endSlot = SLOTS_PER_DAY;
      }

      if (startSlot === endSlot) {
        endSlot = Math.min(startSlot + 1, SLOTS_PER_DAY);
      }

      for (let i = startSlot; i < endSlot; i++) {
        if (i < SLOTS_PER_DAY) {
          daySlots[i] = exception.id;
        }
      }
    });

    dailySlotMap[dateKey] = daySlots;
    currentDate = addDays(currentDate, 1);
  }

  return dailySlotMap;
}

export type TransitionTime = {
  hour: number;
  minute: number;
  isArmed: boolean;
};

// Get actual transition times from schedule rules for a given date
export function getTransitionTimes(
  schedule: AlarmSchedule | undefined,
  exceptions: AlarmException[],
  date: Date,
): TransitionTime[] {
  if (!schedule) return [];

  const dateKey = formatISO(date, { representation: "date" });
  const transitions: TransitionTime[] = [];

  // Check if this date falls within any exception period
  const exception = exceptions.find((ex) => {
    try {
      const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return isDateWithinExceptionDayRange(currentDateOnly, ex.period.start, ex.period.end);
    } catch (error) {
      return false;
    }
  });

  if (exception) {
    // For exceptions, use exception start/end times
    const start = exception.period.start;
    const end = exception.period.end;

    // Create date objects for comparison
    const exStartDate = new Date(start.year, start.month - 1, start.day);
    const exEndDate = new Date(end.year, end.month - 1, end.day);

    // Determine if this is the first day, last day, or a middle day of the exception
    const isFirstDay = formatISO(exStartDate, { representation: "date" }) === dateKey;
    const isLastDay = formatISO(exEndDate, { representation: "date" }) === dateKey;

    const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const isAllDay = isFullDayRangeForDate(currentDateOnly, start, end);

    if (!isAllDay) {
      // Only add transitions when exception actually starts or ends on this day
      if (isFirstDay) {
        transitions.push({
          hour: start.hour,
          minute: start.minute,
          isArmed: false, // Exception overrides schedule
        });
      }
      if (isLastDay) {
        transitions.push({
          hour: end.hour,
          minute: end.minute,
          isArmed: false, // After exception, back to schedule
        });
      }
    }
    return transitions;
  }

  // Get transitions from schedule rules
  const dayOfWeek = date.getDay();

  schedule.rules.forEach((rule) => {
    // Handle weekly_pattern rules
    if (rule.kind === "weekly_pattern") {
      // Check if date is within effective period
      if (!isDateInEffectivePeriod(date, rule.weekly_pattern.effective_period)) {
        return;
      }

      // Convert day strings to numbers and check if this day matches
      const ruleDays = rule.weekly_pattern.days.map((day) => DAY_STRING_TO_NUMBER[day]);
      if (!ruleDays.includes(dayOfWeek)) {
        return;
      }

      // Check if rule is armed based on features
      const isArmed = isRuleArmed(rule.features);

      // Add transition times from time ranges
      const timeRangesArray = normalizeTimeRanges(rule.weekly_pattern.time_ranges);
      addTransitionTimesFromTimeRanges(timeRangesArray, transitions, isArmed);
    } else if (rule.kind === "rotating_pattern") {
      // Handle rotating_pattern rules
      // Check if date is within effective period
      if (!isDateInEffectivePeriod(date, rule.rotating_pattern.effective_period)) {
        return;
      }

      // Find matching interval for this date
      const result = findRotatingPatternInterval(date, rule.rotating_pattern.start, rule.rotating_pattern.pattern);

      if (!result) {
        return; // Invalid pattern or no matching interval
      }

      const matchingInterval = result.interval;

      // Skip if this is an off day
      if (matchingInterval.is_off_day) {
        return;
      }

      // Check if rule is armed based on features
      const isArmed = isRuleArmed(rule.features);

      // Add transition times from time ranges
      if (matchingInterval.time_ranges) {
        const timeRangesArray = normalizeTimeRanges(matchingInterval.time_ranges);
        addTransitionTimesFromTimeRanges(timeRangesArray, transitions, isArmed);
      }
    }
    // Note: absolute_range rules are not handled here (exceptions are handled separately above)
  });

  // Sort transitions by time
  transitions.sort((a, b) => {
    const aMinutes = a.hour * 60 + a.minute;
    const bMinutes = b.hour * 60 + b.minute;
    return aMinutes - bMinutes;
  });

  return transitions;
}

// Helper to check if slot is armed or disarmed (not an exception)
export function isArmedOrDisarmed(slotType: SlotType): boolean {
  return slotType === "armed" || slotType === "disarmed";
}

/**
 * Resolve whether an exception or override segment arms or disarms the alarm.
 * Exceptions are looked up in alarm settings first, then in the schedule's own
 * `exception:`/`override:` rules — expired exceptions are pruned from settings,
 * so on past days the historical schedule's rule is the only record of them.
 */
export function getSegmentAction(
  segType: SlotType,
  exceptions: AlarmException[] | undefined,
  schedule: AlarmSchedule | undefined,
): "armed" | "disarmed" | null {
  if (isArmedOrDisarmed(segType)) return null;
  const exception = exceptions?.find((ex) => ex.id === segType);
  if (exception) return exception.action;
  if (!segType.startsWith("exception:") && !segType.startsWith("override:")) return null;
  const rule = schedule?.rules.find((r) => r.id === segType);
  if (!rule) return null;
  return isRuleArmed(rule.features) ? "armed" : "disarmed";
}

export type LegendStates = {
  armedException: boolean;
  disarmedException: boolean;
  silence: boolean;
  armedOverride: boolean;
  disarmedOverride: boolean;
};

/**
 * Which exception, override and silence states the schedule viewer can draw.
 * Pass the current schedule and any historical schedules: expired exceptions are
 * pruned from alarm settings, so past ones only appear as `exception:` rules.
 */
export function getLegendStates(exceptions: AlarmException[], schedules: AlarmSchedule[]): LegendStates {
  const states: LegendStates = {
    armedException: exceptions.some((ex) => ex.action === "armed"),
    disarmedException: exceptions.some((ex) => ex.action === "disarmed"),
    silence: false,
    armedOverride: false,
    disarmedOverride: false,
  };

  for (const rule of schedules.flatMap((schedule) => schedule.rules)) {
    if (rule.kind !== "absolute_range") continue;
    const isArmed = isRuleArmed(rule.features);
    if (rule.id.startsWith("exception:")) {
      if (isArmed) states.armedException = true;
      else states.disarmedException = true;
    } else if (rule.id.startsWith("override:")) {
      if (isArmed) states.armedOverride = true;
      else states.disarmedOverride = true;
    } else if (rule.id.startsWith("silence:")) {
      states.silence = true;
    }
  }

  return states;
}

/**
 * Shared helper to determine if a camera is currently considered armed
 * based on its arming type (e.g. "schedule_armed", "override_armed", "exception_armed").
 */
export function isArmedFromArmingType(armingType: CameraArmingType | null | undefined): boolean {
  return !!armingType && armingType.endsWith("_armed");
}

// Helper to format time to string (e.g., "6am", "7:30am", "12pm")
export function formatTime(hour: number, minute: number): string {
  const displayHour =
    hour === 0 ? 12
    : hour > 12 ? hour - 12
    : hour;
  const amPm = hour < 12 ? "am" : "pm";
  if (minute === 0) {
    return `${displayHour}${amPm}`;
  }
  return `${displayHour}:${minute.toString().padStart(2, "0")}${amPm}`;
}

/**
 * Compares two schedules for equality by JSON stringifying them.
 * @param schedule1 - First schedule to compare
 * @param schedule2 - Second schedule to compare
 * @returns true if schedules are equal, false otherwise
 */
export function areSchedulesEqual(schedule1: unknown, schedule2: unknown): boolean {
  if (schedule1 === schedule2) return true;
  if (!schedule1 || !schedule2) return false;
  return JSON.stringify(schedule1) === JSON.stringify(schedule2);
}

export type SlotMetadata = {
  slotIndex: number;
  slotType: SlotType;
  isFirstSlot: boolean;
  isLastSlot: boolean;
  showLabel: boolean;
  labelPosition: "top" | "bottom";
  labelTime: { hour: number; minute: number } | null;
};

// Helper to find transition time for a given slot boundary
function findTransitionTime(
  transitionTimes: TransitionTime[],
  slotIndex: number,
  isArmed: boolean,
): { hour: number; minute: number } | null {
  // First try exact slot match
  const exactMatch = transitionTimes.find((t) => {
    const transitionSlot = Math.floor((t.hour * 60 + t.minute) / MINUTES_PER_SLOT);
    return transitionSlot === slotIndex && t.isArmed === isArmed;
  });

  if (exactMatch) {
    return { hour: exactMatch.hour, minute: exactMatch.minute };
  }

  // Fallback: match by hour with correct state
  const hourMatch = transitionTimes.find((t) => t.hour === slotIndex && t.isArmed === isArmed);

  return hourMatch ? { hour: hourMatch.hour, minute: hourMatch.minute } : null;
}

// Calculate metadata for all slots in a day
export function calculateSlotMetadata(slots: DaySlots, transitionTimes: TransitionTime[]): SlotMetadata[] {
  return slots.map((slotType, i) => {
    const prevSlotType = i > 0 ? slots[i - 1] : null;
    const nextSlotType = i < slots.length - 1 ? slots[i + 1] : null;

    // Check for transitions between armed/disarmed states only
    const hasTransitionAtStart =
      prevSlotType !== null &&
      isArmedOrDisarmed(slotType) &&
      isArmedOrDisarmed(prevSlotType) &&
      slotType !== prevSlotType;

    const hasTransitionAtEnd =
      nextSlotType !== null &&
      isArmedOrDisarmed(slotType) &&
      isArmedOrDisarmed(nextSlotType) &&
      slotType !== nextSlotType;

    // Labels are shown in disarmed slots at transition points:
    // - Top: when transitioning from armed to disarmed
    // - Bottom: when transitioning from disarmed to armed
    const showLabelAtTop = hasTransitionAtStart && slotType === "disarmed" && prevSlotType === "armed";
    const showLabelAtBottom = hasTransitionAtEnd && slotType === "disarmed" && nextSlotType === "armed";

    const showLabel = showLabelAtTop || showLabelAtBottom;
    const labelPosition = showLabelAtBottom ? "bottom" : "top";

    // Find the transition time for the appropriate slot boundary
    const labelTime =
      showLabel ? findTransitionTime(transitionTimes, showLabelAtBottom ? i + 1 : i, showLabelAtBottom) : null;

    return {
      slotIndex: i,
      slotType,
      isFirstSlot: i === 0,
      isLastSlot: i === slots.length - 1,
      showLabel,
      labelPosition,
      labelTime,
    };
  });
}

// ─── Percentage-based segment types and functions ────────────────────────────

export type SegmentType = "armed" | "disarmed" | string; // string = exception/silence/override ID

export type DaySegment = {
  type: SegmentType;
  startPercent: number; // 0–100
  endPercent: number; // 0–100
};

export type SegmentLabel = {
  percent: number; // position in the bar (0–100)
  time: { hour: number; minute: number };
  position: "below" | "above"; // whether label sits below or above the boundary (inside the disarmed segment)
  isException: boolean; // whether this boundary involves an exception segment
};

type Transition = { minuteOfDay: number; type: SegmentType };

function minuteToPercent(minute: number): number {
  return (minute / 1440) * 100;
}

/**
 * Collect transition points from schedule rules for a given date.
 * A transition marks where the arming state changes.
 */
function isOverrideOrSilenceRule(rule: AlarmSchedule["rules"][number]): boolean {
  return rule.kind === "absolute_range" && (rule.id.startsWith("override:") || rule.id.startsWith("silence:"));
}

/**
 * Find the end minute of an overnight range on a given day's rules.
 * E.g. if the day has a rule with 18:00→06:00, returns 360 (the "tail" that
 * spills into the next day). Returns 0 if no overnight range is found.
 */
function findOvernightTailForDay(schedule: AlarmSchedule, date: Date, filter: "routine" | "override"): number {
  const dayOfWeek = date.getDay();
  let maxEndMin = 0;
  for (const rule of schedule.rules) {
    const isOverride = isOverrideOrSilenceRule(rule);
    if (filter === "routine" && isOverride) continue;
    if (filter === "override" && !isOverride) continue;

    if (rule.kind === "weekly_pattern") {
      if (!isDateInEffectivePeriod(date, rule.weekly_pattern.effective_period)) continue;
      const ruleDays = rule.weekly_pattern.days.map((day) => DAY_STRING_TO_NUMBER[day]);
      if (!ruleDays.includes(dayOfWeek)) continue;
      if (!isRuleArmed(rule.features)) continue;
      const timeRangesArray = normalizeTimeRanges(rule.weekly_pattern.time_ranges);
      for (const tr of timeRangesArray) {
        if (tr.kind === "time_range" && tr.start && tr.end) {
          const startMin = tr.start.hour * 60 + tr.start.minute;
          const endMin = tr.end.hour * 60 + tr.end.minute;
          if (startMin > endMin && endMin > 0) {
            maxEndMin = Math.max(maxEndMin, endMin);
          }
        }
      }
    }
  }
  return maxEndMin;
}

/**
 * Collect overnight spillover transitions for the current day.
 * Looks at the *previous* day's rules to find overnight ranges that spill
 * past midnight into the current day (0→endMin).
 */
function collectPreviousDaySpillover(
  schedule: AlarmSchedule,
  date: Date,
  filter: "routine" | "override",
): Transition[] {
  const previousDate = addDays(date, -1);
  const prevDayOfWeek = previousDate.getDay();
  const spilloverTransitions: Transition[] = [];

  schedule.rules.forEach((rule) => {
    const isOverride = isOverrideOrSilenceRule(rule);
    if (filter === "routine" && isOverride) return;
    if (filter === "override" && !isOverride) return;

    if (rule.kind === "weekly_pattern") {
      if (!isDateInEffectivePeriod(previousDate, rule.weekly_pattern.effective_period)) return;
      const ruleDays = rule.weekly_pattern.days.map((day) => DAY_STRING_TO_NUMBER[day]);
      if (!ruleDays.includes(prevDayOfWeek)) return;
      const isArmed = isRuleArmed(rule.features);
      if (!isArmed) return;
      const type: SegmentType = "armed";
      const oppositeType: SegmentType = "disarmed";
      const timeRangesArray = normalizeTimeRanges(rule.weekly_pattern.time_ranges);

      timeRangesArray.forEach((tr) => {
        if (tr.kind === "all_day") {
          // Previous day was armed all day — armed at midnight, so look for an
          // overnight range on the current day whose tail (0→endMin) continues it
          const currentDayEndMin = findOvernightTailForDay(schedule, date, filter);
          if (currentDayEndMin > 0) {
            spilloverTransitions.push({ minuteOfDay: 0, type });
            spilloverTransitions.push({ minuteOfDay: currentDayEndMin, type: oppositeType });
          }
        } else if (tr.kind === "time_range" && tr.start && tr.end) {
          const startMin = tr.start.hour * 60 + tr.start.minute;
          const endMin = tr.end.hour * 60 + tr.end.minute;
          if (startMin > endMin && endMin > 0) {
            // Previous day's overnight range spills into current day: 0→endMin
            spilloverTransitions.push({ minuteOfDay: 0, type });
            spilloverTransitions.push({ minuteOfDay: endMin, type: oppositeType });
          }
        }
      });
    }
  });

  return spilloverTransitions;
}

function collectRuleTransitions(schedule: AlarmSchedule, date: Date, filter: "routine" | "override"): Transition[] {
  const transitions: Transition[] = [];
  const dayOfWeek = date.getDay();

  // First, collect spillover from the previous day's overnight rules
  const spillover = collectPreviousDaySpillover(schedule, date, filter);
  transitions.push(...spillover);

  schedule.rules.forEach((rule) => {
    // Filter: "routine" skips overrides/silences, "override" only includes them
    const isOverride = isOverrideOrSilenceRule(rule);
    if (filter === "routine" && isOverride) return;
    if (filter === "override" && !isOverride) return;

    if (rule.kind === "weekly_pattern") {
      if (!isDateInEffectivePeriod(date, rule.weekly_pattern.effective_period)) return;
      const ruleDays = rule.weekly_pattern.days.map((day) => DAY_STRING_TO_NUMBER[day]);
      if (!ruleDays.includes(dayOfWeek)) return;
      const isArmed = isRuleArmed(rule.features);
      const type: SegmentType = isArmed ? "armed" : "disarmed";
      const oppositeType: SegmentType = isArmed ? "disarmed" : "armed";
      const timeRangesArray = normalizeTimeRanges(rule.weekly_pattern.time_ranges);

      timeRangesArray.forEach((tr) => {
        if (tr.kind === "all_day") {
          transitions.push({ minuteOfDay: 0, type });
          transitions.push({ minuteOfDay: 1440, type: oppositeType });
        } else if (tr.kind === "time_range" && tr.start && tr.end) {
          const startMin = tr.start.hour * 60 + tr.start.minute;
          const endMin = tr.end.hour * 60 + tr.end.minute;
          if (startMin === endMin) return;
          if (startMin > endMin) {
            // Overnight range: only emit the current-day portion (startMin→midnight)
            transitions.push({ minuteOfDay: startMin, type });
            transitions.push({ minuteOfDay: 1440, type: oppositeType });
          } else {
            transitions.push({ minuteOfDay: startMin, type });
            transitions.push({ minuteOfDay: endMin, type: oppositeType });
          }
        }
      });
    } else if (rule.kind === "rotating_pattern") {
      if (!isDateInEffectivePeriod(date, rule.rotating_pattern.effective_period)) return;
      const result = findRotatingPatternInterval(date, rule.rotating_pattern.start, rule.rotating_pattern.pattern);
      if (!result || result.interval.is_off_day) return;
      const isArmed = isRuleArmed(rule.features);
      const type: SegmentType = isArmed ? "armed" : "disarmed";
      if (result.interval.time_ranges) {
        const timeRangesArray = normalizeTimeRanges(result.interval.time_ranges);
        timeRangesArray.forEach((tr) => {
          if (tr.kind === "all_day") {
            transitions.push({ minuteOfDay: 0, type });
            transitions.push({ minuteOfDay: 1440, type: isArmed ? "disarmed" : "armed" });
          } else if (tr.kind === "time_range" && tr.start && tr.end) {
            const startMin = tr.start.hour * 60 + tr.start.minute;
            const endMin = tr.end.hour * 60 + tr.end.minute;
            if (startMin === endMin) return;
            if (startMin > endMin) {
              // Overnight range: only emit the current-day portion (startMin→midnight)
              transitions.push({ minuteOfDay: startMin, type });
              transitions.push({ minuteOfDay: 1440, type: isArmed ? "disarmed" : "armed" });
              // NOTE: previous-day spillover for rotating patterns is not handled here yet
              // because it would require resolving the previous day's rotating interval
            } else {
              transitions.push({ minuteOfDay: startMin, type });
              transitions.push({ minuteOfDay: endMin, type: isArmed ? "disarmed" : "armed" });
            }
          }
        });
      }
    } else if (rule.kind === "absolute_range") {
      const ep = rule.absolute_range.effective_period;
      if (!isDateInEffectivePeriod(date, ep)) return;
      const start = ep.start;
      const end = ep.end;
      if (!start || !end) return;

      const dateKey = formatISO(date, { representation: "date" });
      const ruleStartDate = new Date(start.year, start.month - 1, start.day);
      const ruleEndDate = new Date(end.year, end.month - 1, end.day);
      const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      const isAllDay = isFullDayRangeForDate(currentDateOnly, start, end);
      const isFirstDay = formatISO(ruleStartDate, { representation: "date" }) === dateKey;
      const isLastDay = formatISO(ruleEndDate, { representation: "date" }) === dateKey;

      let startMin: number;
      let endMin: number;
      if (isAllDay) {
        startMin = 0;
        endMin = 1440;
      } else if (isFirstDay && isLastDay) {
        startMin = start.hour * 60 + start.minute;
        endMin = end.hour * 60 + end.minute;
      } else if (isFirstDay) {
        startMin = start.hour * 60 + start.minute;
        endMin = 1440;
      } else if (isLastDay) {
        startMin = 0;
        endMin = end.hour * 60 + end.minute;
      } else {
        startMin = 0;
        endMin = 1440;
      }

      transitions.push({ minuteOfDay: startMin, type: rule.id });
      transitions.push({ minuteOfDay: endMin, type: "disarmed" });
    }
  });

  return transitions;
}

/**
 * Collect transition points from exceptions for a given date.
 * Exceptions overlay on top of schedule rules.
 */
function collectExceptionTransitions(exceptions: AlarmException[], date: Date): Transition[] {
  const transitions: Transition[] = [];
  const dateKey = formatISO(date, { representation: "date" });
  const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  exceptions.forEach((exception) => {
    const start = exception.period.start;
    const end = exception.period.end;

    if (!isDateWithinExceptionDayRange(currentDateOnly, start, end)) return;

    const exStartDate = new Date(start.year, start.month - 1, start.day);
    const exEndDate = new Date(end.year, end.month - 1, end.day);

    const isAllDay = isFullDayRangeForDate(currentDateOnly, start, end);
    const isFirstDay = formatISO(exStartDate, { representation: "date" }) === dateKey;
    const isLastDay = formatISO(exEndDate, { representation: "date" }) === dateKey;

    let startMin: number;
    let endMin: number;
    if (isAllDay) {
      startMin = 0;
      endMin = 1440;
    } else if (isFirstDay && isLastDay) {
      startMin = start.hour * 60 + start.minute;
      endMin = end.hour * 60 + end.minute;
    } else if (isFirstDay) {
      startMin = start.hour * 60 + start.minute;
      endMin = 1440;
    } else if (isLastDay) {
      startMin = 0;
      endMin = end.hour * 60 + end.minute;
    } else {
      startMin = 0;
      endMin = 1440;
    }

    // Ensure minimum 1-minute segment for same-slot exceptions
    if (startMin === endMin) {
      endMin = Math.min(startMin + 1, 1440);
    }

    transitions.push({ minuteOfDay: startMin, type: exception.id });
    transitions.push({ minuteOfDay: endMin, type: "disarmed" });
  });

  return transitions;
}

/**
 * Build percentage-based segments for a single day.
 *
 * Replaces `buildScheduleFromRules` for the DayColumn use-case, producing
 * minute-precision segments expressed as percentages of the day (0 = midnight,
 * 100 = next midnight).
 */
export function buildDaySegments(schedule: AlarmSchedule, exceptions: AlarmException[], date: Date): DaySegment[] {
  // Priority order (highest last, so later steps overwrite earlier ones):
  //   1. Routine rules (weekly_pattern, rotating_pattern)
  //   2. Exceptions
  //   3. Overrides & silences (absolute_range rules with override:/silence: IDs)

  // Step 1: Build baseline from routine rules (excludes overrides/silences)
  const ruleTransitions = collectRuleTransitions(schedule, date, "routine");

  const baseline: SegmentType[] = new Array(1440).fill("disarmed");

  // Apply ranges — each rule pushes paired transitions (start, end).
  // Do NOT sort globally, as that breaks pairing when multiple rules share the
  // same minuteOfDay. Pairs are already consecutive per rule; later rules
  // naturally override earlier ones in the baseline array.
  for (let i = 0; i < ruleTransitions.length - 1; i += 2) {
    const startT = ruleTransitions[i];
    const endT = ruleTransitions[i + 1];
    if (!endT) break;
    const from = Math.max(0, Math.min(startT.minuteOfDay, 1440));
    const to = Math.max(0, Math.min(endT.minuteOfDay, 1440));
    for (let m = from; m < to; m++) {
      baseline[m] = startT.type;
    }
  }

  // Step 2: Layer exception transitions on top
  const exTransitions = collectExceptionTransitions(exceptions, date);
  for (let i = 0; i < exTransitions.length - 1; i += 2) {
    const startT = exTransitions[i];
    const endT = exTransitions[i + 1];
    const from = Math.max(0, Math.min(startT.minuteOfDay, 1440));
    const to = endT ? Math.max(0, Math.min(endT.minuteOfDay, 1440)) : 1440;
    for (let m = from; m < to; m++) {
      baseline[m] = startT.type;
    }
  }

  // Step 3: Layer overrides and silences on top (highest priority)
  const overrideTransitions = collectRuleTransitions(schedule, date, "override");
  for (let i = 0; i < overrideTransitions.length - 1; i += 2) {
    const startT = overrideTransitions[i];
    const endT = overrideTransitions[i + 1];
    if (!endT) break;
    const from = Math.max(0, Math.min(startT.minuteOfDay, 1440));
    const to = Math.max(0, Math.min(endT.minuteOfDay, 1440));
    for (let m = from; m < to; m++) {
      baseline[m] = startT.type;
    }
  }

  // Step 4: Convert minute-resolution array to segments by run-length encoding
  const segments: DaySegment[] = [];
  let currentType = baseline[0];
  let startMinute = 0;

  for (let m = 1; m <= 1440; m++) {
    const type = m < 1440 ? baseline[m] : null; // null forces final flush
    if (type !== currentType) {
      segments.push({
        type: currentType,
        startPercent: minuteToPercent(startMinute),
        endPercent: minuteToPercent(m),
      });
      if (type !== null) {
        currentType = type;
        startMinute = m;
      }
    }
  }

  return segments;
}

/**
 * Derive label positions from segments. Labels appear at boundaries between
 * "armed" and "disarmed" segments (same visual logic as the previous slot-based
 * approach). Exception/silence/override boundaries don't get labels.
 */
export function getSegmentLabels(segments: DaySegment[]): SegmentLabel[] {
  const labels: SegmentLabel[] = [];

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const prevSeg = i > 0 ? segments[i - 1] : null;

    // Label at the start of this segment if there's a type transition
    if (prevSeg && seg.type !== prevSeg.type) {
      const isException = !isArmedOrDisarmed(seg.type);
      const prevIsException = !isArmedOrDisarmed(prevSeg.type);

      // Skip if both sides are armed/disarmed with same type (no transition) — already handled by !== check above
      // Show labels for: armed↔disarmed, exception↔armed, exception↔disarmed
      const showLabel =
        (isArmedOrDisarmed(seg.type) && isArmedOrDisarmed(prevSeg.type)) || isException || prevIsException;

      // Skip labels for all-day exceptions (exception covers 0–100%)
      const exceptionIsAllDay =
        (isException && seg.startPercent === 0 && seg.endPercent === 100) ||
        (prevIsException && prevSeg.startPercent === 0 && prevSeg.endPercent === 100);

      if (showLabel && !exceptionIsAllDay) {
        const percent = seg.startPercent;
        const totalMinutes = Math.round((percent / 100) * 1440);
        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;

        // Skip labels at 23:59 — it's effectively end-of-day (24:00) which doesn't show a label
        if (hour === 23 && minute === 59) continue;

        // Exception/override times sit outside the exception — start above, end below — so a
        // short exception never shows its start time beneath its end time.
        // Routine times sit on the disarmed side: armed→disarmed below, disarmed→armed above.
        let position: "above" | "below";
        if (isException) position = "above";
        else if (prevIsException) position = "below";
        else position = seg.type === "armed" ? "above" : "below";
        labels.push({ percent, time: { hour, minute }, position, isException: isException || prevIsException });
      }
    }
  }

  return labels;
}

const WEEKDAYS: DayOfTheWeek[] = ["MON", "TUE", "WED", "THU", "FRI"];
const WEEKENDS: DayOfTheWeek[] = ["SAT", "SUN"];

/**
 * Collapses routine windows by grouping weekdays and weekends when they have identical time ranges.
 * - If all weekdays (Mon-Fri) have the same time ranges, they are collapsed to a single "Weekdays" entry.
 * - If both weekend days (Sat-Sun) have the same time ranges, they are collapsed to a single "Weekends" entry.
 *
 * @param windows - Array of weekly routine windows to process
 * @returns Array of windows with collapsed weekdays/weekends where applicable
 */
export function collapseRoutineWindows(windows: WeeklyRoutineWindow[]): WeeklyRoutineWindow[] {
  if (windows.length === 0) return windows;

  // Helper to compare time ranges for equality
  const timeRangesEqual = (a: WeeklyRoutineWindow["timeRanges"], b: WeeklyRoutineWindow["timeRanges"]): boolean => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  // Collect all days and their time ranges
  const dayToTimeRanges = new Map<string, WeeklyRoutineWindow["timeRanges"]>();

  windows.forEach((window) => {
    window.days.forEach((day) => {
      const existing = dayToTimeRanges.get(day);
      // If day already exists with different time ranges, we can't collapse
      if (existing && !timeRangesEqual(existing, window.timeRanges)) {
        dayToTimeRanges.set(day, null as unknown as WeeklyRoutineWindow["timeRanges"]); // Mark as conflict
      } else if (!existing) {
        dayToTimeRanges.set(day, window.timeRanges);
      }
    });
  });

  // Check if all weekdays have the same time ranges (and no conflicts)
  const weekdayTimeRanges = WEEKDAYS.map((day) => dayToTimeRanges.get(day)).filter(Boolean);
  const canCollapseWeekdays =
    weekdayTimeRanges.length === WEEKDAYS.length &&
    weekdayTimeRanges.every((tr) => tr && timeRangesEqual(tr, weekdayTimeRanges[0]!));

  // Check if both weekends have the same time ranges (and no conflicts)
  const weekendTimeRanges = WEEKENDS.map((day) => dayToTimeRanges.get(day)).filter(Boolean);
  const canCollapseWeekends =
    weekendTimeRanges.length === WEEKENDS.length &&
    weekendTimeRanges.every((tr) => tr && timeRangesEqual(tr, weekendTimeRanges[0]!));

  // Build the result array
  const result: WeeklyRoutineWindow[] = [];
  const processedDays = new Set<string>();

  // Add collapsed weekdays if applicable
  if (canCollapseWeekdays && weekdayTimeRanges[0]) {
    result.push({
      name: "Weekdays",
      days: [...WEEKDAYS] as DaysOfTheWeek,
      timeRanges: weekdayTimeRanges[0],
    });
    WEEKDAYS.forEach((day) => processedDays.add(day));
  }

  // Add remaining windows that contain unprocessed days (excluding weekends - they go last)
  windows.forEach((window) => {
    const hasUnprocessedDays = window.days.some((day) => !processedDays.has(day));
    const hasWeekendDays = window.days.some((day) => WEEKENDS.includes(day));
    // Skip windows that only contain weekend days - they'll be handled later
    if (hasUnprocessedDays && !hasWeekendDays) {
      // Filter out processed days from this window
      const remainingDays = window.days.filter((day) => !processedDays.has(day));
      if (remainingDays.length > 0) {
        result.push({
          name: window.name,
          days: remainingDays as DaysOfTheWeek,
          timeRanges: window.timeRanges,
        });
        remainingDays.forEach((day) => processedDays.add(day));
      }
    } else if (hasUnprocessedDays && hasWeekendDays) {
      // Window has both weekend and non-weekend days - split them
      const nonWeekendDays = window.days.filter((day) => !processedDays.has(day) && !WEEKENDS.includes(day));
      const weekendDays = window.days.filter((day) => !processedDays.has(day) && WEEKENDS.includes(day));
      if (nonWeekendDays.length > 0) {
        result.push({
          name: undefined,
          days: nonWeekendDays as DaysOfTheWeek,
          timeRanges: window.timeRanges,
        });
        nonWeekendDays.forEach((day) => processedDays.add(day));
      }
      // Weekend days will be handled in the next section
    }
  });

  // Add collapsed weekends last if applicable
  if (canCollapseWeekends && weekendTimeRanges[0]) {
    result.push({
      name: "Weekends",
      days: [...WEEKENDS] as DaysOfTheWeek,
      timeRanges: weekendTimeRanges[0],
    });
  } else {
    // If weekends weren't collapsed, add any remaining weekend windows
    windows.forEach((window) => {
      const hasUnprocessedWeekendDays = window.days.some((day) => !processedDays.has(day) && WEEKENDS.includes(day));
      if (hasUnprocessedWeekendDays) {
        const remainingDays = window.days.filter((day) => !processedDays.has(day));
        if (remainingDays.length > 0) {
          const originalWindowHadBothTypes = window.days.some((day) => !WEEKENDS.includes(day));
          result.push({
            name: originalWindowHadBothTypes ? undefined : window.name,
            days: remainingDays as DaysOfTheWeek,
            timeRanges: window.timeRanges,
          });
        }
      }
    });
  }

  return result;
}
